import { Save, Delete as Trash2 } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';

import { FloatingSaveButton } from '@/components/floating-save-button';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { Page } from '@/components/page';
import { useEditSceneForm } from '../hooks/useEditSceneForm';
import { EventPopupLoader } from './EventPopupLoader';
import { SceneMainFields } from './SceneMainFields';
import { SceneMetaFields } from './SceneMetaFields';

interface SceneEditFormProps {
  sceneId: string;
}

export function SceneEditForm({ sceneId }: SceneEditFormProps) {
  const {
    novel,
    t,
    showDeleteConfirm,
    setShowDeleteConfirm,
    selectedEventForPopup,
    setSelectedEventForPopup,
    sceneData,
    isLoadingScene,
    isSaving,
    isDeleting,
    characters,
    locations,
    organizations,
    events,
    register,
    control,
    handleSubmit,
    errors,
    selectedCharacterNames,
    selectedOrganizationNames,
    selectedEvents,
    handleDelete,
  } = useEditSceneForm({ sceneId });

  if (isLoadingScene || !novel.id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Page
        title={t('entity_form.scenes.edit_dynamic_title', { name: sceneData?.name || t('entity_form.scenes.breadcrumb_scene') })}
        description={t('entity_form.scenes.edit_desc')}
        breadcrumbs={[
          { label: t('entity_list.scenes.title'), href: '/scenes' },
          { label: sceneData?.name || t('entity_form.scenes.breadcrumb_scene'), href: `/scenes/view/${sceneId}` },
          { label: t('entity_form.common.edit') }
        ]}
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="error"
              disabled={isSaving || isDeleting}
              onClick={() => setShowDeleteConfirm(true)}
              startIcon={<Trash2 sx={{ fontSize: 18 }} />}
            >
              {t('entity_form.common.delete')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving || isDeleting}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
            >
              {t('entity_form.scenes.save_changes')}
            </Button>
          </Stack>
        }
      >

        <DeleteConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
          itemName={sceneData?.name}
          description={t('entity_form.scenes.delete_desc')}
        />

        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardHeader
            title={t('entity_form.scenes.scene_details')}
            titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
          />
          <CardContent>
            <Stack spacing={4}>
              <SceneMainFields
                register={register}
                control={control}
                errors={errors}
                characters={characters}
                locations={locations}
                organizations={organizations}
                events={events}
                selectedEvents={selectedEvents}
                selectedCharacterNames={selectedCharacterNames}
                selectedOrganizationNames={selectedOrganizationNames}
                setSelectedEventForPopup={setSelectedEventForPopup}
              />
              <SceneMetaFields register={register} />
            </Stack>
          </CardContent>
        </Card>
      </Page>

      <FloatingSaveButton
        isSaving={isSaving}
        disabled={isDeleting}
        label={t('entity_form.scenes.save_changes')}
      />

      {selectedEventForPopup && (
        <EventPopupLoader
          eventId={selectedEventForPopup.id}
          open={!!selectedEventForPopup}
          onClose={() => setSelectedEventForPopup(null)}
          locations={locations}
          characters={characters}
          organizations={organizations}
        />
      )}
    </form>
  );
}
export default SceneEditForm;
